import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface ICreateNinVerificationRequest {
  nin: string;
  firstName: string;
  lastName: string;
  userPhotoUrl: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  gender: string;
}

interface ICreateNinVerificationResponse {
  success?: boolean;
  message?: string;
  data?: unknown;
  errors?: IApiResponseError[];
}

const initialState: IApiState<ICreateNinVerificationResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createNinVerificationAsync = createAsyncThunk(
  'ninVerification/create',
  async (
    values: ICreateNinVerificationRequest,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      };

      const { data } = await api.post(
        '/representative-nin-verification',
        values,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ICreateNinVerificationResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const ninVerificationCreationSlice = createSlice({
  name: 'ninVerification/create',
  initialState,
  reducers: {
    resetNinVerification: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createNinVerificationAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createNinVerificationAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ICreateNinVerificationResponse;
      })
      .addCase(createNinVerificationAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ICreateNinVerificationResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetNinVerification } = ninVerificationCreationSlice.actions;

export default ninVerificationCreationSlice.reducer;
